import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { APP_CONFIG } from '@application/config';
import { CertificateResponse } from '../entities/academy.model';
import {
  ACADEMY_ENUM_WIRE_CODES,
  CertificateTargetType,
} from '../entities/academy-enums.model';
import {
  CertificateVerificationResponse,
  IssuedCertificateItem,
  MineResult,
  toMineResult,
} from '../entities/academy-responses.model';

/**
 * HTTP client for the Certificates area endpoints (`/Certificates/*`).
 * Source of truth: `docs/academy-api-integration.md` §7.11 (and `IssuedCertificateItem` in
 * `docs/academy-api.types.ts`). All three are dedicated custom routes: `/mine` (holder),
 * `/issued` (producer) and `/verify` (anonymous). Never read producer certificates from
 * `/odata/Certificates`: since backend spec 011 it ignores `VerificationCode` (400 when a
 * query option names it) and returns only the caller's own tenant.
 */
@Injectable({ providedIn: 'root' })
export class CertificatesService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = inject(APP_CONFIG).apiUrl.academy_service;

  /**
   * `GET /Certificates/mine` — the viewer's own certificates. Plain array.
   * 🔑 identity from token: never send a `PersonId`.
   */
  public mine(): Observable<CertificateResponse[]> {
    return this.http.get<CertificateResponse[]>(
      `${this.baseUrl}/Certificates/mine`,
      { withCredentials: true },
    );
  }

  /**
   * `GET /Certificates/issued` — producer view of every certificate issued under the caller's
   * scope. Server-side `search` (holder name, case-insensitive contains), `certificateTargetType`
   * (the category — taken by NAME and converted here to its NUMERIC wire code through
   * `ACADEMY_ENUM_WIRE_CODES`: Course = 1, LearningPath = 2; acceptance of the name on the wire
   * is unverified, so it never goes out) and `top`/`skip` paging; absent options are omitted so
   * the server defaults apply (`top` 20, `skip` 0; `top` <= 0, `top` > 200 or `skip` < 0 → 422 —
   * nothing is capped silently). Wire is a PascalCase `{ Items, Total }` envelope with a numeric
   * `CertificateTargetType` and the server-resolved `TargetTitle`, folded into `MineResult` by
   * `toMineResult` (which maps the enum code to its name). There is no sort parameter; the
   * server's `targetId` (one specific course/path) is not exposed here because nothing consumes
   * it yet.
   * ⚠ 403 when the caller is not a producer — checked before paging, so 403 wins over 422.
   */
  public issued(
    query: {
      search?: string;
      certificateTargetType?: CertificateTargetType;
      top?: number;
      skip?: number;
    } = {},
  ): Observable<MineResult<IssuedCertificateItem>> {
    let params = new HttpParams();
    const search = query.search?.trim();
    if (search) params = params.set('search', search);
    if (query.certificateTargetType) {
      params = params.set(
        'certificateTargetType',
        String(
          ACADEMY_ENUM_WIRE_CODES['CertificateTargetType'][
            query.certificateTargetType
          ],
        ),
      );
    }
    if (query.top != null) params = params.set('top', String(query.top));
    if (query.skip != null) params = params.set('skip', String(query.skip));
    return this.http
      .get<unknown>(`${this.baseUrl}/Certificates/issued`, {
        params,
        withCredentials: true,
      })
      .pipe(map((res) => toMineResult<IssuedCertificateItem>(res)));
  }

  /**
   * `GET /certificates/verify/{code}` — anonymous verification (lowercase route).
   * No token: never set `withCredentials`. Always 200; an invalid code yields
   * `{ Valid: false }`.
   */
  public verify(code: string): Observable<CertificateVerificationResponse> {
    return this.http.get<CertificateVerificationResponse>(
      `${this.baseUrl}/certificates/verify/${encodeURIComponent(code)}`,
    );
  }
}
